import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { staffPersonaQueryKey, useStaffPersona } from "../queries";
import { Avatar, AvatarProps } from "@mantine/core";
import { getPortalsApiResourceUrl } from "@services/api/portalApi";

export const StaffAvatar: React.FC<{ identifier: { value: string; type: string }; size?: number; avatarProps?: AvatarProps }> = ({ identifier, size = 32, avatarProps }) => {
    const qc = useQueryClient();
    const { data, isLoading } = useStaffPersona({ identifier: identifier.value, identifierType: identifier.type as 'initials' | 'staffId' | 'oid' });

    // Track retry/failure by stable resource path (without token query params).
    const failedResourcesRef = React.useRef<Set<string>>(new Set());
    const refreshedResourcesRef = React.useRef<Set<string>>(new Set());

    // The actual src we render (sticky).
    const [imgSrc, setImgSrc] = React.useState<string | null>(null);

    const photoUrl = data?.photo?.url ?? null;
    const photoResource = photoUrl ? photoUrl.split("?")[0] : null;

    // When we get a NEW signed URL, use it unless this resource is known-bad.
    React.useEffect(() => {
        if (!photoUrl) {
            setImgSrc(null);
            return;
        }

        if (photoResource && failedResourcesRef.current.has(photoResource)) {
            setImgSrc(null);
            return;
        }

        setImgSrc(photoUrl);
    }, [photoUrl, photoResource]);

    if (isLoading || !data) {
        return (
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ width: size, height: size, borderRadius: "50%", background: "rgba(0,0,0,0.08)" }} />
            <div style={{ width: 140, height: 12, borderRadius: 6, background: "rgba(0,0,0,0.08)" }} />
        </div>
        );
    }

    return (
        <Avatar 
            name={`${data.firstName} ${data.surname}`} 
            color='initials'
            src={imgSrc ? getPortalsApiResourceUrl(imgSrc) : undefined}
            onError={() => {
                // Always show fallback immediately.
                setImgSrc(null);

                if (!photoResource) {
                    return;
                }

                // Retry once for this resource to recover from expired signatures.
                if (!refreshedResourcesRef.current.has(photoResource)) {
                    refreshedResourcesRef.current.add(photoResource);
                    qc.invalidateQueries({ queryKey: staffPersonaQueryKey({ identifier: identifier.value, identifierType: identifier.type as 'initials' | 'staffId' | 'oid' }) });
                    return;
                }

                // Permanent failure for this resource in this component instance.
                failedResourcesRef.current.add(photoResource);
            }}
            component="button"
            {...avatarProps}
        />
    );
}
